import { useCallback, useEffect, useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";
import * as THREE from "three";


export function DraggableAxisWindow({ rotation }: { rotation: { rotateX: number; rotateY: number; rotateZ: number } }) {
  const [position, setPosition] = useState({ x: 20, y: 100 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setDragging(true);
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (dragging) {
      setPosition({
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y,
      });
    }
  }, [dragging]);

  const onMouseUp = () => setDragging(false);

  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    } else {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [dragging, onMouseMove]);

  return (
    <div
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: 'clamp(120px, 20vw, 200px)',
        aspectRatio: '1 / 1',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        borderRadius: 10,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        border: '1px solid rgba(0,0,0,0.1)',
        overflow: 'hidden',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        zIndex: 20,
        cursor: dragging ? 'grabbing' : 'grab',
      }}
      onMouseDown={onMouseDown}
    >
      <Canvas
        orthographic
        camera={{ zoom: 50, position: [0, 0, 100] }}
        gl={{ preserveDrawingBuffer: true }}
      >
        <group
          rotation={[
            THREE.MathUtils.degToRad(rotation.rotateX),
            THREE.MathUtils.degToRad(rotation.rotateY),
            THREE.MathUtils.degToRad(rotation.rotateZ),
          ]}
        >
          <axesHelper args={[1.5]} />
          <gridHelper args={[5, 5]} />
        </group>
      </Canvas>
    </div>
  );
}